"use client";

import { useActionState, useState, type ReactNode } from "react";

import { devLoginAction, loginAction } from "@/src/actions/auth";

export function LoginForm({ devShortcuts = false }: { devShortcuts?: boolean }) {
  const [error, formAction, pending] = useActionState(loginAction, undefined);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <section
      className="relative w-full max-w-[555px] overflow-hidden rounded-[22px] border border-[rgba(177,199,220,0.22)] bg-[linear-gradient(180deg,rgba(20,28,37,0.91),rgba(7,12,17,0.94))] px-6 py-8 shadow-[0_28px_80px_rgba(0,0,0,0.52),inset_0_1px_0_rgba(255,255,255,0.035)] backdrop-blur-[14px] sm:px-[42px] sm:pt-[38px] sm:pb-[34px]"
      aria-labelledby="login-title"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-[-1px] bg-[linear-gradient(120deg,rgba(255,255,255,0.08),transparent_24%,transparent_75%,rgba(221,165,74,0.06))] [mask-image:linear-gradient(#000,transparent_28%)]"
      />

      <div className="relative text-center">
        <p className="mb-2 text-[10px] font-bold tracking-[0.22em] text-[#f2d28c] uppercase">
          Área segura
        </p>
        <h1
          id="login-title"
          className="text-[clamp(25px,3vw,31px)] font-bold tracking-[-0.03em]"
        >
          Acesse sua conta
        </h1>
        <p className="mt-2.5 text-sm leading-relaxed text-[#9ca8b6]">
          Continue para sua plataforma OBANKER.
        </p>
      </div>

      <form action={formAction} className="relative mt-[30px]">
        <label className="relative mb-[15px] flex h-[58px] items-center rounded-[10px] border border-[rgba(191,208,224,0.2)] bg-white/[0.045] transition focus-within:border-[rgba(225,183,107,0.7)] focus-within:bg-white/[0.055] focus-within:shadow-[0_0_0_3px_rgba(205,151,67,0.11)]">
          <span className="sr-only">E-mail</span>
          <FieldIcon>
            <path d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm0 2 8 6 8-6" />
          </FieldIcon>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            placeholder="seu@email.com"
            required
            className="h-full min-w-0 flex-1 border-0 bg-transparent pr-3.5 text-[#f7f8fa] outline-none placeholder:text-[#97a3af]"
          />
        </label>

        <label className="relative mb-[15px] flex h-[58px] items-center rounded-[10px] border border-[rgba(191,208,224,0.2)] bg-white/[0.045] transition focus-within:border-[rgba(225,183,107,0.7)] focus-within:bg-white/[0.055] focus-within:shadow-[0_0_0_3px_rgba(205,151,67,0.11)]">
          <span className="sr-only">Senha</span>
          <FieldIcon>
            <path d="M7 10V8a5 5 0 0 1 10 0v2M6 10h12a2 2 0 0 1 2 2v7H4v-7a2 2 0 0 1 2-2Z" />
          </FieldIcon>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="Sua senha"
            required
            className="h-full min-w-0 flex-1 border-0 bg-transparent pr-1.5 text-[#f7f8fa] outline-none placeholder:text-[#97a3af]"
          />
          <button
            type="button"
            className="grid h-full w-[52px] place-items-center border-0 bg-transparent text-[#c9d2dc] hover:text-[#f2d28c]"
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            aria-pressed={showPassword}
            onClick={() => setShowPassword((visible) => !visible)}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </label>

        <div className="mt-[19px] mb-[22px] flex items-center justify-between gap-3.5 text-[13px] max-[440px]:text-xs">
          <label className="inline-flex cursor-pointer items-center gap-2 text-[#d8dee6]">
            <input
              name="remember"
              type="checkbox"
              defaultChecked
              className="peer sr-only"
            />
            <span className="grid size-[18px] place-items-center rounded border border-[#6d7884] bg-white/[0.025] peer-checked:border-[#f2d28c] peer-checked:bg-[linear-gradient(135deg,#f2d28c,#c79443)] peer-checked:[&_svg]:opacity-100">
              <svg viewBox="0 0 20 20" className="size-[13px] opacity-0" aria-hidden="true">
                <path
                  d="m4 10 4 4 8-9"
                  fill="none"
                  stroke="#06101a"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span>Lembrar de mim</span>
          </label>
          <a
            href="/#contato"
            className="text-right font-semibold text-[#e1b76d] no-underline hover:text-[#f4d89e] hover:underline hover:underline-offset-4"
          >
            Esqueci minha senha?
          </a>
        </div>

        {error ? (
          <p className="mb-3 text-center text-xs text-[#f3b4b4]" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={pending}
          className="inline-flex min-h-14 w-full cursor-pointer items-center justify-center gap-3 rounded-[10px] border border-[rgba(255,232,178,0.42)] bg-[linear-gradient(180deg,#e6c17b_0%,#b77a30_100%)] font-bold text-[#111418] shadow-[0_12px_30px_rgba(177,116,43,0.16),inset_0_1px_0_rgba(255,255,255,0.55)] transition hover:-translate-y-px hover:brightness-[1.07] disabled:cursor-wait disabled:opacity-80"
        >
          <span>{pending ? "Entrando..." : "Entrar"}</span>
          <svg viewBox="0 0 24 24" className="size-[19px]" aria-hidden="true">
            <path
              d="M5 12h14M14 7l5 5-5 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>

      {devShortcuts ? <DevShortcuts /> : null}
    </section>
  );
}

function DevShortcuts() {
  return (
    <div className="relative mt-6 border-t border-white/10 pt-5">
      <p className="mb-3 text-center text-[10px] font-bold tracking-[0.18em] text-[#9ca8b6] uppercase">
        Acesso rápido · desenvolvimento
      </p>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <DevShortcut role="ADMIN" label="Painel do administrador" />
        <DevShortcut role="PARCEIRO" label="Painel do parceiro" />
      </div>
    </div>
  );
}

function DevShortcut({
  role,
  label,
}: {
  role: "ADMIN" | "PARCEIRO";
  label: string;
}) {
  const [error, formAction, pending] = useActionState(devLoginAction, undefined);

  return (
    <form action={formAction}>
      <input type="hidden" name="role" value={role} />
      <button
        type="submit"
        disabled={pending}
        className="min-h-11 w-full cursor-pointer rounded-[10px] border border-[rgba(213,164,79,0.45)] bg-white/[0.03] px-3 text-sm font-semibold text-[#f2d28c] transition hover:border-[#f2d28c] hover:bg-white/[0.06] disabled:cursor-wait disabled:opacity-70"
      >
        {pending ? "Entrando..." : label}
      </button>
      {error ? (
        <p className="mt-2 text-center text-xs text-[#f3b4b4]" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function FieldIcon({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-full w-[50px] place-items-center text-[#c2ccd6]">
      <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {children}
        </g>
      </svg>
    </span>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
      <path
        d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 10.6A2.5 2.5 0 0 0 12 14.5 2.5 2.5 0 0 0 14.5 12M6.1 6.3C3.8 7.8 2 12 2 12s3.5 6 10 6c1.7 0 3.2-.4 4.5-1M9.9 5.2A11 11 0 0 1 12 5c6.5 0 10 7 10 7s-.8 1.4-2.2 2.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
